import React, { useEffect, useMemo, useState } from 'react'
import { getCreditHistory } from '../../features/OrdersSlice'
import Loading from '../../components/styled/Loading'
import { Table, Input, Button } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { makeOrderResolved } from '../../features/ReceiptingSlice'
import styled from 'styled-components'
import {
  antdTableHorizontalScroll,
  visibleHorizontalScroll,
} from 'src/components/styled/horizontalScroll'
import CreditNotesColumns from './CreditNotesColums'
import ResolvedNotesColumns from './ResolvedNotesColumn'
import CreditNotesCSV from './CreditNotesCSV'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import { useHistory, useLocation, Link } from 'react-router-dom'
import { ASK_FOR_CREDIT_BRIDGE_COPY } from 'src/utils/askForCreditBridgeHelpers'
import {
  CREDIT_REQUEST_VIEW,
  countOpenCreditAsksFromHistory,
  countOpenCreditRequestsFromHistory,
  countOpenReceiptIssuesFromHistory,
  openCreditAskCutoffNote,
  filterCreditHistoryByView,
  purchaseOrderHasAppliedCreditNote,
} from 'src/utils/askForCreditBridgeHelpers'
import { mapCreditHistoryRowToTableRecord } from 'src/utils/creditRequestTableHelpers'
import { mapInvoiceToTableRow } from 'src/utils/invoiceUploadHelpers'
import {
  buildInvoicesPath,
  fetchOpenCreditRequestCounts,
} from 'src/utils/reconciliationHealthApi'
import { getUploadedInvoices } from '../../features/NewInvoicesSlice'
import { API } from 'src/utils/interceptor'

const { Search } = Input

const CreditNotes = props => {
  const blocked = useIsBlocked('credit-notes')
  const location = useLocation()
  const history = useHistory()

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const dispatch = useDispatch()
  const { creditNotes, isCreditHistoryLoading, creditNotesPagination } =
    useSelector(state => state.orders)
  const { venueId, initialView } = props
  const uploadedInvoices = useSelector(
    state => state.newInvoices?.uploadedInvoices || [],
  )
  const invoiceRows = useMemo(
    () =>
      (uploadedInvoices || []).map(invoice => mapInvoiceToTableRow(invoice)),
    [uploadedInvoices],
  )

  const queryView = useMemo(() => {
    const params = new URLSearchParams(location.search || '')
    const view = params.get('view')
    if (view === CREDIT_REQUEST_VIEW.RESOLVED || view === 'resolved') {
      return CREDIT_REQUEST_VIEW.RESOLVED
    }
    if (
      view === CREDIT_REQUEST_VIEW.RECEIPT_ISSUES ||
      view === 'receipt-issues' ||
      view === 'issues'
    ) {
      return CREDIT_REQUEST_VIEW.RECEIPT_ISSUES
    }
    if (initialView === 'resolved') return CREDIT_REQUEST_VIEW.RESOLVED
    if (location.pathname?.includes('resolvednotes')) {
      return CREDIT_REQUEST_VIEW.RESOLVED
    }
    return CREDIT_REQUEST_VIEW.OPEN
  }, [location.search, location.pathname, initialView])

  /** Ollie / deep-link `?q=` seeds the PO search (exact match on PON). */
  const querySearch = useMemo(() => {
    const params = new URLSearchParams(location.search || '')
    return String(params.get('q') || '').trim()
  }, [location.search])

  const [viewMode, setViewMode] = useState(queryView)
  const [dataSourse, setDataSourse] = useState([])
  const [searchQuery, setSearchQuery] = useState(querySearch)
  const [isBtnDisabled, setIsBtnDisabled] = useState(false)
  const [openCreditCounts, setOpenCreditCounts] = useState(null)
  const [chaseById, setChaseById] = useState({})

  useEffect(() => {
    setViewMode(queryView)
  }, [queryView])

  useEffect(() => {
    if (!venueId || !Array.isArray(creditNotes) || !creditNotes.length) {
      return undefined
    }
    const receiptingIds = creditNotes
      .map(note => note?._id?.receipting?.[0]?._id)
      .filter(Boolean)
    if (!receiptingIds.length) return undefined
    let cancelled = false
    API.post('api2', `receipting/${venueId}/credit-requests/chases`, {
      body: { receiptingIds },
    })
      .then(result => {
        if (cancelled) return null
        const next = {}
        for (const row of result?.records || []) {
          next[String(row.receiptingId)] = row.creditChase
        }
        setChaseById(current => ({ ...current, ...next }))
        return null
      })
      .catch(() => null)
    return () => {
      cancelled = true
    }
  }, [venueId, creditNotes])

  useEffect(() => {
    if (querySearch) setSearchQuery(querySearch)
  }, [querySearch])

  useEffect(() => {
    dispatch(getCreditHistory(venueId, creditNotesPagination))
    if (!venueId) return undefined

    dispatch(getUploadedInvoices(venueId))
    let cancelled = false

    const loadOpenCount = async () => {
      try {
        const counts = await fetchOpenCreditRequestCounts({
          venueId,
          apiGet: API.get,
        })
        if (!cancelled) setOpenCreditCounts(counts)
      } catch {
        if (!cancelled) setOpenCreditCounts(null)
      }
    }

    loadOpenCount()
    return () => {
      cancelled = true
    }
  }, [venueId])

  useEffect(() => {
    if (searchQuery === '') {
      setIsBtnDisabled(true)
    } else {
      setIsBtnDisabled(false)
    }
  }, [searchQuery])

  // The tab counts every open ask it lists; the amber nav badge only counts
  // asks since the alert cut-off. Fall back to the loaded page until the
  // dedicated counts return.
  const displayOpenCreditAskCount =
    openCreditCounts?.open ?? countOpenCreditAsksFromHistory(creditNotes)
  const openCreditCutoffNote = openCreditAskCutoffNote({
    open: displayOpenCreditAskCount,
    newSinceCutoff:
      openCreditCounts?.newSinceCutoff ??
      countOpenCreditRequestsFromHistory(creditNotes),
  })
  const openReceiptIssueCount = countOpenReceiptIssuesFromHistory(creditNotes)
  const sourceNotes = filterCreditHistoryByView(creditNotes, viewMode)

  useEffect(() => {
    filterCreditNotes(sourceNotes)
  }, [creditNotes, viewMode, invoiceRows])

  const makeOrderResolvedValue = async receiptingId => {
    await dispatch(makeOrderResolved(receiptingId, venueId))
    if (!venueId) return
    try {
      const counts = await fetchOpenCreditRequestCounts({
        venueId,
        apiGet: API.get,
      })
      setOpenCreditCounts(counts)
    } catch {
      // Keep the last known count if refresh fails.
    }
  }

  const setCreditView = nextView => {
    setViewMode(nextView)
    setSearchQuery('')
    const base = `/v/${venueId}/purchasing/creditnotes`
    if (nextView === CREDIT_REQUEST_VIEW.RESOLVED) {
      history.replace(`${base}?view=resolved`)
    } else if (nextView === CREDIT_REQUEST_VIEW.RECEIPT_ISSUES) {
      history.replace(`${base}?view=receipt-issues`)
    } else {
      history.replace(base)
    }
  }

  const filterCreditNotes = creditNotesList => {
    const filteredArr = creditNotesList.map(orderNote =>
      mapCreditHistoryRowToTableRecord(orderNote, {
        invoices: invoiceRows,
        creditNoteApplied: purchaseOrderHasAppliedCreditNote(
          orderNote?._id?.purchaseOrderNumber,
          invoiceRows,
        ),
      }),
    )

    setDataSourse(filteredArr)
  }

  const onSearch = () => {
    const filteredArr = dataSourse.filter(el => el.PON === searchQuery)
    setDataSourse(filteredArr)
  }

  const isResolvedView = viewMode === CREDIT_REQUEST_VIEW.RESOLVED
  const isReceiptIssuesView = viewMode === CREDIT_REQUEST_VIEW.RECEIPT_ISSUES
  const isOpenCreditAsksView = viewMode === CREDIT_REQUEST_VIEW.OPEN

  return (
    <>
      {isCreditHistoryLoading ? (
        <Loading />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              flex: '1 1 100%',
              minWidth: 0,
            }}
          >
            {isOpenCreditAsksView ? (
              <TeachingPanel>
                <TeachingTitle>
                  {ASK_FOR_CREDIT_BRIDGE_COPY.teachingTitle}
                </TeachingTitle>
                <TeachingBody>
                  {ASK_FOR_CREDIT_BRIDGE_COPY.teachingBody.map(paragraph => (
                    <TeachingCopy key={paragraph.slice(0, 24)}>
                      {paragraph}
                    </TeachingCopy>
                  ))}
                  <TeachingCopy>
                    Upload a credit note in{' '}
                    <Link to={buildInvoicesPath(venueId, { upload: true })}>
                      Invoices &amp; Credit Notes
                    </Link>
                    , or keep working on{' '}
                    <Link to={`/v/${venueId}/purchasing/receipting`}>
                      Goods receipting
                    </Link>
                    .
                  </TeachingCopy>
                </TeachingBody>
              </TeachingPanel>
            ) : isReceiptIssuesView ? (
              <TeachingPanel>
                <TeachingTitle>Receipt issues (no credit asked)</TeachingTitle>
                <TeachingCopy>
                  {ASK_FOR_CREDIT_BRIDGE_COPY.receiptIssuesTeaching}
                </TeachingCopy>
              </TeachingPanel>
            ) : (
              <p style={{ marginBottom: 16, color: '#595959', maxWidth: 720 }}>
                Resolved credit asks are kept here for reference. Open credit
                asks stay on Open requests and drive the amber badge.
              </p>
            )}

            <ViewToggleRow>
              <Button
                type={isOpenCreditAsksView ? 'primary' : 'default'}
                onClick={() => setCreditView(CREDIT_REQUEST_VIEW.OPEN)}
              >
                Open requests
                {displayOpenCreditAskCount > 0
                  ? ` (${displayOpenCreditAskCount})`
                  : ''}
              </Button>
              <Button
                type={isResolvedView ? 'primary' : 'default'}
                onClick={() => setCreditView(CREDIT_REQUEST_VIEW.RESOLVED)}
              >
                Resolved
              </Button>
              <Button
                type={isReceiptIssuesView ? 'primary' : 'default'}
                onClick={() =>
                  setCreditView(CREDIT_REQUEST_VIEW.RECEIPT_ISSUES)
                }
              >
                Receipt issues
                {openReceiptIssueCount > 0 ? ` (${openReceiptIssueCount})` : ''}
              </Button>
            </ViewToggleRow>
            {isOpenCreditAsksView && openCreditCutoffNote ? (
              <CutoffNote data-testid='open-credit-cutoff-note'>
                {openCreditCutoffNote}
              </CutoffNote>
            ) : null}

            <TableWrapper>
              <StyledSearchBarWrapper>
                <Search
                  size='large'
                  placeholder='Search by PO Number'
                  style={{ width: 300 }}
                  type='text'
                  value={searchQuery}
                  allowClear
                  onPressEnter={onSearch}
                  onChange={e => {
                    setSearchQuery(e.target.value)
                    if (e.target.value === '') {
                      filterCreditNotes(sourceNotes)
                    }
                  }}
                />
                <Button
                  disabled={isBtnDisabled}
                  onClick={onSearch}
                  style={{
                    opacity: isBtnDisabled ? 0.4 : 1,
                    color: '#ffffff',
                    backgroundColor: '#b5d922',
                    borderColor: '#b5d922',
                    height: 40,
                    width: 80,
                  }}
                >
                  Search
                </Button>

                <div style={{ marginLeft: 'auto' }}>
                  <CreditNotesCSV creditNotes={dataSourse} />
                </div>
              </StyledSearchBarWrapper>

              <StyledTable
                dataSource={dataSourse.map((order, index) => ({
                  key: index,
                  ...order,
                }))}
                columns={
                  isResolvedView
                    ? ResolvedNotesColumns()
                    : CreditNotesColumns(makeOrderResolvedValue, {
                        venueId,
                        chaseById,
                        onChased: (receiptingId, creditChase) =>
                          setChaseById(current => ({
                            ...current,
                            [String(receiptingId)]: creditChase,
                          })),
                      })
                }
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: isOpenCreditAsksView
                    ? ASK_FOR_CREDIT_BRIDGE_COPY.emptyOpen
                    : isReceiptIssuesView
                    ? ASK_FOR_CREDIT_BRIDGE_COPY.emptyReceiptIssues
                    : ASK_FOR_CREDIT_BRIDGE_COPY.emptyResolved,
                }}
                pagination={{
                  position: 'both',
                  total: dataSourse.length,
                  pageSize: creditNotesPagination.limit,
                  pageSizeOptions: ['10', '20', '50', '100', '250'],
                  showSizeChanger: true,
                  current: creditNotesPagination.page,
                  onChange: page => {
                    dispatch(
                      getCreditHistory(venueId, {
                        page: page,
                        limit: creditNotesPagination.limit,
                      }),
                    )
                  },
                  onShowSizeChange: (_, pageSize) =>
                    dispatch(
                      getCreditHistory(venueId, {
                        page: 1,
                        limit: pageSize,
                      }),
                    ),
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} results`,
                }}
              />
            </TableWrapper>
          </div>
        </>
      )}
    </>
  )
}

const ViewToggleRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`

const CutoffNote = styled.p`
  margin: -8px 0 16px;
  font-size: 13px;
  color: #8c8c8c;
`

const TeachingPanel = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
`

const TeachingTitle = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #874d00;
`

const TeachingBody = styled.div`
  @media (min-width: 960px) {
    column-count: 2;
    column-gap: 28px;
  }
`

const TeachingCopy = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.75);
  break-inside: avoid;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    font-weight: 600;
    color: #ad6800;
    text-decoration: underline;
  }
`

const StyledTable = styled(Table)`
  .ant-table-thead {
    white-space: nowrap;
  }
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    border-right: 1px solid #e8e8e8;
  }

  ${antdTableHorizontalScroll}
`
const TableWrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  text-align: center;
  position: relative;
  ${visibleHorizontalScroll}
  ${antdTableHorizontalScroll}
`

const StyledSearchBarWrapper = styled.div`
  display: flex;
  gap: 5px;
`

export default CreditNotes
